import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { FunctionsHttpError, type Session } from "@supabase/supabase-js";
import { GameButton } from "../components/GameButton";
import { VerifiedCreatorMark } from "../components/VerifiedCreatorMark";
import { NumgnumCard } from "../components/NumgnumCard";
import { NUMGNUM_NAMES, type Edition } from "../lib/cards";
import { hasSupabaseConfiguration, supabase, type PublicCard } from "../lib/supabase";

type Profile = {
  id: string;
  username: string;
  account_status: "guest" | "saved";
  play_points: number;
  win_points: number;
  wins: number;
  draws: number;
  losses: number;
};

type AccountCard = PublicCard & { id: string; state?: string };
type SealedPack = {
  id: string;
  player_id: string;
  edition: Edition;
  source: "app_store" | "web" | "alpha_recovery" | "admin_adjustment";
  source_reference: string;
  acquired_at: string;
};
type Entitlement = { player_id: string; item: string };
type CardKind = "all" | "shiny" | "founder";
type LoginMode = "sign-in" | "sign-up" | "forgot-password";
type PendingWebsiteSignup = { userID: string; username: string; termsVersion: string };

const websiteTermsVersion = "2026-09-04";
const pendingWebsiteSignupKey = "numgnums.pending-website-signup";

const editionOrder: Edition[] = ["arcade", "midnight", "coastal", "library"];
const kindOrder: CardKind[] = ["all", "shiny", "founder"];
const kindLabels: Record<CardKind, string> = {
  all: "All Cards",
  shiny: "Only Shiny",
  founder: "Only Founders",
};

const entitlementCatalog = [
  { id: "remote_computer", title: "Remote Computer", detail: "Fast travel between every island building.", icon: "⌁" },
  { id: "auto_forge", title: "Auto-Forge", detail: "Watches exact pairs and forges while you roam.", icon: "⚒" },
  { id: "fishing_auto_keep", title: "Fishing Auto-Keep", detail: "Keeps card catches without interrupting the fishing loop.", icon: "◖" },
  { id: "music_sunshine_strum", title: "Sunshine Strum", detail: "A brighter daytime musician track.", icon: "☀" },
  { id: "music_seaside_brass", title: "Seaside Brass", detail: "The island musician's after-hours brass set.", icon: "♫" },
] as const;

const packCatalog = [
  { edition: "arcade" as const, title: "Five Arcade Packs", art: "/art/packs/arcade-pack.png", tone: "fire" as const },
  { edition: "midnight" as const, title: "Five Midnight Packs", art: "/art/packs/midnight-pack.png", tone: "violet" as const },
];

async function checkoutErrorMessage(error: unknown) {
  if (error instanceof FunctionsHttpError) {
    const payload = await error.context.json().catch(() => null) as { error?: unknown } | null;
    if (typeof payload?.error === "string") return payload.error;
  }
  return error instanceof Error ? error.message : "Pack checkout could not open.";
}

function nextValue<T>(values: T[], current: T) {
  return values[(values.indexOf(current) + 1) % values.length];
}

function displayEdition(edition: Edition) {
  if (edition === "library") return "Library Plate";
  return edition.charAt(0).toUpperCase() + edition.slice(1);
}

function cardOrder(left: AccountCard, right: AccountCard) {
  if (left.level !== right.level) return left.level - right.level;
  const editionDifference = editionOrder.indexOf(left.edition) - editionOrder.indexOf(right.edition);
  if (editionDifference !== 0) return editionDifference;
  if (left.serial_number !== right.serial_number) return left.serial_number - right.serial_number;
  if (left.is_shiny !== right.is_shiny) return left.is_shiny ? -1 : 1;
  return left.id.localeCompare(right.id);
}

function normalizedPlayerName(value: string) {
  return value.trim();
}

function validPlayerName(value: string) {
  return /^[A-Za-z][A-Za-z0-9]{2,15}$/.test(normalizedPlayerName(value));
}

function readPendingWebsiteSignup(): PendingWebsiteSignup | null {
  try {
    const stored = window.localStorage.getItem(pendingWebsiteSignupKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<PendingWebsiteSignup>;
    if (typeof parsed.userID !== "string" || typeof parsed.username !== "string" || parsed.termsVersion !== websiteTermsVersion) return null;
    return parsed as PendingWebsiteSignup;
  } catch {
    return null;
  }
}

function storePendingWebsiteSignup(signup: PendingWebsiteSignup | null) {
  try {
    if (signup) window.localStorage.setItem(pendingWebsiteSignupKey, JSON.stringify(signup));
    else window.localStorage.removeItem(pendingWebsiteSignupKey);
  } catch {
    // Private browsing and strict storage settings must not block account creation.
  }
}

function SignupTerms({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <>
      <details className="signup-terms signup-form__wide">
        <summary>Read the Terms of Use · Effective September 4, 2026</summary>
        <div>
          <p><strong>Eligibility.</strong> Saved accounts and purchases are available to players age 13 or older. Any higher local age or guardian requirement also applies.</p>
          <p><strong>Your island.</strong> Keep your sign-in secure and choose a player name that does not reveal private contact information.</p>
          <p><strong>Packs and purchases.</strong> Paid packs contain randomized virtual cards. Virtual cards, points, and upgrades have no cash value and cannot be redeemed outside NUMGNUMS. The applicable store’s billing and refund rules also apply.</p>
          <p><strong>Battles and forging.</strong> Some battles can transfer cards. Forging permanently consumes eligible input cards. The game shows the applicable stakes, cost, or outcome before confirmation.</p>
          <p><strong>Fair play.</strong> Do not cheat, automate play, exploit bugs, harass others, impersonate another player, or use a hateful, sexual, threatening, or privacy-violating name.</p>
          <p><strong>Changes and availability.</strong> NUMGNUMS may rebalance gameplay, add or retire features, or temporarily interrupt service. Material changes will be presented before they apply.</p>
          <p><strong>Legal.</strong> The game is provided as available to the extent permitted by law. These terms do not remove consumer rights that cannot legally be waived.</p>
        </div>
      </details>
      <label className="signup-consent signup-form__wide">
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} required />
        <span>I am 13 or older, meet any higher local age requirement, and agree to the current Terms of Use. My email stays private.</span>
      </label>
    </>
  );
}

export function AccountPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupUsername, setSignupUsername] = useState("");
  const [signupPasswordConfirmation, setSignupPasswordConfirmation] = useState("");
  const [acceptsSignupTerms, setAcceptsSignupTerms] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [accountSetupUserID, setAccountSetupUserID] = useState<string | null>(null);
  const [loginMode, setLoginMode] = useState<LoginMode>("sign-in");
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [confirmedRecoveryPassword, setConfirmedRecoveryPassword] = useState("");
  const [passwordSignInAvailable, setPasswordSignInAvailable] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmedNewPassword, setConfirmedNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [cards, setCards] = useState<AccountCard[]>([]);
  const [battleHoardIDs, setBattleHoardIDs] = useState<Set<string>>(new Set());
  const [sealedPacks, setSealedPacks] = useState<SealedPack[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [packInventoryOnline, setPackInventoryOnline] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(Boolean(supabase));
  const [checkoutEdition, setCheckoutEdition] = useState<Edition | null>(null);
  const [editionFilter, setEditionFilter] = useState<Edition | "all">("all");
  const [hidesDuplicates, setHidesDuplicates] = useState(false);
  const [cardKind, setCardKind] = useState<CardKind>("all");
  const [combatHoardOnly, setCombatHoardOnly] = useState(false);
  const collectionHeading = useRef<HTMLDivElement>(null);
  const pendingSignupUsername = useRef<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      setIsLoadingAccount(false);
      return;
    }

    let active = true;
    let openingId: string | null = null;
    const signupNameFor = (session: Session) => {
      if (pendingSignupUsername.current) return pendingSignupUsername.current;

      const stored = readPendingWebsiteSignup();
      if (stored?.userID === session.user.id) return stored.username;

      const metadata = session.user.user_metadata as Record<string, unknown>;
      if (metadata.website_signup_terms_version === websiteTermsVersion && metadata.website_signup_completed !== true) {
        return typeof metadata.website_signup_username === "string" ? metadata.website_signup_username : null;
      }
      return null;
    };

    const openSession = (session: Session) => {
      const id = session.user.id;
      if (openingId === id) return;
      openingId = id;
      const pendingName = signupNameFor(session);
      window.setTimeout(() => {
        if (!active) return;
        void (async () => {
          let completedSignup = false;
          try {
            if (pendingName) {
              await finishWebsiteSignup(pendingName);
              pendingSignupUsername.current = null;
              storePendingWebsiteSignup(null);
              window.history.replaceState({}, "", "/account");
              completedSignup = true;
            }
            const loaded = await loadAccount(id);
            if (loaded && completedSignup) {
              setMessage("ISLAND SAVED · Your starter Hoard is forged and ready here and in the game.");
            }
          } catch (error) {
            await supabase?.auth.signOut();
            clearAccount();
            setMessage(error instanceof Error ? error.message : "Your island could not be saved.");
            setIsLoadingAccount(false);
          } finally {
            if (active) openingId = null;
          }
        })();
      }, 0);
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
        setMessage(null);
      }
      setPasswordSignInAvailable(Boolean(session?.user.identities?.some((identity) => identity.provider === "email")));
      if (session) {
        setIsLoadingAccount(true);
        openSession(session);
      } else {
        clearAccount();
        setIsLoadingAccount(false);
      }
    });

    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setMessage(error.message);
        setIsLoadingAccount(false);
      } else if (data.session) {
        setPasswordSignInAvailable(Boolean(data.session.user.identities?.some((identity) => identity.provider === "email")));
        openSession(data.session);
      } else {
        setPasswordSignInAvailable(false);
        setIsLoadingAccount(false);
      }
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get("checkout");
    if (checkout === "cancelled") setMessage("Checkout closed. Nothing was charged and no packs were added.");
    if (checkout === "success") setMessage("Payment received. The forge clerk is adding five sealed packs to your hoard…");
  }, []);

  async function loadAccount(id: string): Promise<boolean> {
    if (!supabase) return false;
    setIsLoadingAccount(true);
    try {
      // The native game performs this same claim before it reads player data.
      // It is a no-op for ordinary accounts, but reconnects configured legacy
      // accounts such as Thatcher to their canonical profile and inventory.
      const claimResult = await supabase.rpc("claim_configured_admin_account");
      if (claimResult.error) throw claimResult.error;
      const claimedProfile = claimResult.data as { id?: string } | null;
      const accountID = claimedProfile?.id ?? id;

      const [profileResult, cardResult, discoveryResult, entitlementResult, packResult, deckResult] = await Promise.all([
        supabase.from("profiles").select("id,username,account_status,play_points,win_points,wins,draws,losses").eq("id", accountID).single(),
        supabase.from("card_instances").select("id,level,edition,is_shiny,serial_number,state").eq("owner_id", accountID).neq("state", "forged").order("level").order("edition").order("serial_number"),
        supabase.from("numnum_discoveries").select("level,edition,card_id,founder_username"),
        supabase.from("player_entitlements").select("player_id,item").eq("player_id", accountID),
        supabase.from("player_sealed_packs").select("id,player_id,edition,source,source_reference,acquired_at").eq("player_id", accountID).is("opened_at", null).order("acquired_at"),
        supabase.from("battle_decks").select("id,battle_deck_cards(card_id)").eq("owner_id", accountID).maybeSingle(),
      ]);
      const requiredError = profileResult.error ?? cardResult.error ?? discoveryResult.error ?? entitlementResult.error ?? deckResult.error;
      if (requiredError || !profileResult.data) {
        setMessage(requiredError?.message ?? "The hoard would not open.");
        return false;
      }

      const discoveries = new Map((discoveryResult.data ?? []).map((row) => [`${row.level}:${row.edition}`, row]));
      const profileData = profileResult.data as Profile;
      if (profileData.account_status !== "saved") {
        setProfile(null);
        setCards([]);
        setBattleHoardIDs(new Set());
        setSealedPacks([]);
        setEntitlements([]);
        setAccountSetupUserID(accountID);
        setMessage(null);
        return true;
      }

      setAccountSetupUserID(null);
      setProfile(profileData);
      setPassword("");
      setCards((cardResult.data ?? []).map((card) => {
        const discovery = discoveries.get(`${card.level}:${card.edition}`);
        return {
          ...card,
          username: profileData.username,
          is_founder: discovery?.card_id === card.id,
          founder_username: discovery?.founder_username ?? "THE FORGE",
        };
      }) as AccountCard[]);
      const deckRows = (deckResult.data?.battle_deck_cards ?? []) as { card_id: string }[];
      setBattleHoardIDs(new Set(deckRows.map((row) => row.card_id)));
      setEntitlements((entitlementResult.data ?? []) as Entitlement[]);
      setPackInventoryOnline(!packResult.error);
      setSealedPacks(packResult.error ? [] : (packResult.data ?? []) as SealedPack[]);

      const checkout = new URLSearchParams(window.location.search).get("checkout");
      if (checkout === "success") {
        const checkoutSessionID = new URLSearchParams(window.location.search).get("session_id");
        const packsDelivered = Boolean(checkoutSessionID) && (packResult.data ?? []).some(
          (pack) => pack.source === "web" && pack.source_reference === checkoutSessionID,
        );
        setMessage(packsDelivered
          ? "Five sealed packs are now in your hoard and will appear in MY HOARD in the game."
          : "Payment received. Stripe is still handing the bundle to the forge clerk; refresh in a moment.");
        window.history.replaceState({}, "", "/account");
      } else if (!packResult.error) {
        setMessage(null);
      }
      return true;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The hoard would not open.");
      return false;
    } finally {
      setIsLoadingAccount(false);
    }
  }

  function clearAccount() {
    setAccountSetupUserID(null);
    setProfile(null);
    setCards([]);
    setBattleHoardIDs(new Set());
    setSealedPacks([]);
    setEntitlements([]);
    setPasswordSignInAvailable(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmedNewPassword("");
    setPasswordMessage(null);
  }

  async function signIn(event: FormEvent) {
    event.preventDefault();
    if (!supabase) { setMessage("The island signal is offline. Try again soon."); return; }
    setMessage("Opening your hoard…");
    setIsLoadingAccount(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setMessage(error.message);
      setIsLoadingAccount(false);
    }
  }

  async function finishWebsiteSignup(username: string, marksEmailSignupComplete = true) {
    if (!supabase) throw new Error("The island signal is offline. Try again soon.");
    const cleanedUsername = normalizedPlayerName(username);
    if (!validPlayerName(cleanedUsername)) {
      throw new Error("Choose 3 to 16 letters and numbers, beginning with a letter.");
    }

    const starterResult = await supabase.rpc("bootstrap_guest_player");
    if (starterResult.error) throw starterResult.error;
    const accountResult = await supabase.rpc("finalize_player_account", {
      p_username: cleanedUsername,
      p_terms_version: websiteTermsVersion,
    });
    if (accountResult.error) throw accountResult.error;
    if (marksEmailSignupComplete) {
      const completionResult = await supabase.auth.updateUser({ data: { website_signup_completed: true } });
      if (completionResult.error) throw completionResult.error;
    }
  }

  async function signUp(event: FormEvent) {
    event.preventDefault();
    if (!supabase || isSigningUp) {
      if (!supabase) setMessage("The island signal is offline. Try again soon.");
      return;
    }

    const cleanedUsername = normalizedPlayerName(signupUsername);
    if (!validPlayerName(cleanedUsername)) {
      setMessage("Choose 3 to 16 letters and numbers, beginning with a letter.");
      return;
    }
    if (password.length < 8) {
      setMessage("Your password needs at least 8 characters.");
      return;
    }
    if (password !== signupPasswordConfirmation) {
      setMessage("The passwords do not match.");
      return;
    }
    if (!acceptsSignupTerms) {
      setMessage("Confirm that you are eligible and accept the Terms of Use before saving your island.");
      return;
    }

    setIsSigningUp(true);
    setMessage("Claiming your island name…");
    pendingSignupUsername.current = cleanedUsername;
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/account?signup=complete`,
          data: {
            username: cleanedUsername,
            display_name: cleanedUsername,
            full_name: cleanedUsername,
            website_signup_username: cleanedUsername,
            website_signup_terms_version: websiteTermsVersion,
          },
        },
      });
      if (error) throw error;
      if (data.user) {
        storePendingWebsiteSignup({ userID: data.user.id, username: cleanedUsername, termsVersion: websiteTermsVersion });
      }
      if (data.session) {
        setMessage("Forging your starter Hoard…");
      } else {
        pendingSignupUsername.current = null;
        setPassword("");
        setSignupPasswordConfirmation("");
        setMessage("CHECK YOUR EMAIL · Open the confirmation link to finish saving your island and forge your starter Hoard.");
      }
    } catch (error) {
      pendingSignupUsername.current = null;
      storePendingWebsiteSignup(null);
      setMessage(error instanceof Error ? error.message : "Your island could not be saved.");
    } finally {
      setIsSigningUp(false);
    }
  }

  async function completeConnectedAccount(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !accountSetupUserID || isSigningUp) return;

    const cleanedUsername = normalizedPlayerName(signupUsername);
    if (!validPlayerName(cleanedUsername)) {
      setMessage("Choose 3 to 16 letters and numbers, beginning with a letter.");
      return;
    }
    if (!acceptsSignupTerms) {
      setMessage("Confirm that you are eligible and accept the Terms of Use before saving your island.");
      return;
    }

    setIsSigningUp(true);
    setMessage("Saving your Apple-connected island…");
    try {
      await finishWebsiteSignup(cleanedUsername, false);
      const loaded = await loadAccount(accountSetupUserID);
      if (loaded) {
        setSignupUsername("");
        setAcceptsSignupTerms(false);
        setMessage("ISLAND SAVED · Your starter Hoard is forged and ready here and in the game.");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Your island could not be saved.");
    } finally {
      setIsSigningUp(false);
    }
  }

  async function sendPasswordReset(event: FormEvent) {
    event.preventDefault();
    if (!supabase || isSendingPasswordReset) {
      if (!supabase) setMessage("The island signal is offline. Try again soon.");
      return;
    }

    setIsSendingPasswordReset(true);
    setMessage("Sending a recovery flare…");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/account`,
      });
      if (error) throw error;
      setMessage("RECOVERY FLARE SENT · If that email belongs to a NUMGNUMS account, its recovery link is on the way.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The recovery flare could not be sent.");
    } finally {
      setIsSendingPasswordReset(false);
    }
  }

  async function completePasswordRecovery(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    if (recoveryPassword.length < 8) {
      setMessage("Your new password needs at least 8 characters.");
      return;
    }
    if (recoveryPassword !== confirmedRecoveryPassword) {
      setMessage("The new passwords do not match.");
      return;
    }

    setIsChangingPassword(true);
    setMessage("Forging your new island key…");
    try {
      const { error } = await supabase.auth.updateUser({ password: recoveryPassword });
      if (error) throw error;
      setRecoveryPassword("");
      setConfirmedRecoveryPassword("");
      setIsPasswordRecovery(false);
      setMessage("PASSWORD RESET · Your new island key is ready.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The password could not be reset.");
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function cancelPasswordRecovery() {
    await supabase?.auth.signOut();
    setRecoveryPassword("");
    setConfirmedRecoveryPassword("");
    setIsPasswordRecovery(false);
    setLoginMode("sign-in");
    setMessage(null);
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !profile || !passwordSignInAvailable || isChangingPassword) return;
    if (!currentPassword) {
      setPasswordMessage("Enter your current password.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage("Your new password needs at least 8 characters.");
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordMessage("Choose a new password that is different from the current one.");
      return;
    }
    if (newPassword !== confirmedNewPassword) {
      setPasswordMessage("The new passwords do not match.");
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage("Checking the old key…");
    try {
      const { data: userResult, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const accountEmail = userResult.user.email;
      if (!accountEmail) throw new Error("This account does not have an email password to change.");

      const { error: verificationError } = await supabase.auth.signInWithPassword({
        email: accountEmail,
        password: currentPassword,
      });
      if (verificationError) {
        if (verificationError.message.toLowerCase().includes("invalid login credentials")) {
          throw new Error("The current password is incorrect.");
        }
        throw verificationError;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setCurrentPassword("");
      setNewPassword("");
      setConfirmedNewPassword("");
      setPasswordMessage("PASSWORD CHANGED · Your new island key is ready.");
    } catch (error) {
      setPasswordMessage(error instanceof Error ? error.message : "The password could not be changed.");
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function signOut() {
    await supabase?.auth.signOut();
    clearAccount();
  }

  async function signInWithApple() {
    if (!supabase) { setMessage("The island signal is offline. Try again soon."); return; }
    setMessage("Opening Apple sign in…");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: `${window.location.origin}/account` },
    });
    if (error) setMessage(error.message);
  }

  async function buyPackBundle(edition: Edition) {
    if (!supabase || !profile) return;
    setCheckoutEdition(edition);
    setMessage(`Opening secure checkout for five ${displayEdition(edition)} packs…`);
    try {
      const { data: refreshedAuth, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshedAuth.session) {
        throw new Error("Your NUMGNUMS login has expired. Sign in again to open secure checkout.");
      }
      const { data, error } = await supabase.functions.invoke("create-pack-checkout", { body: { edition } });
      if (error) throw error;
      if (!data?.url) throw new Error(data?.error ?? "Checkout did not return a secure payment page.");
      window.location.assign(data.url);
    } catch (error) {
      setCheckoutEdition(null);
      setMessage(await checkoutErrorMessage(error));
    }
  }

  const availableEditions = useMemo(() => {
    const present = new Set(cards.map((card) => card.edition));
    return editionOrder.filter((edition) => present.has(edition));
  }, [cards]);

  const visibleCards = useMemo(() => {
    let result = cards.filter((card) => {
      if (editionFilter !== "all" && card.edition !== editionFilter) return false;
      if (cardKind === "shiny" && !card.is_shiny) return false;
      if (cardKind === "founder" && !card.is_founder) return false;
      if (combatHoardOnly && !battleHoardIDs.has(card.id)) return false;
      return true;
    });

    if (hidesDuplicates) {
      const groups = new Map<string, AccountCard[]>();
      result.forEach((card) => {
        const key = `${card.level}:${card.edition}`;
        groups.set(key, [...(groups.get(key) ?? []), card]);
      });
      result = [...groups.values()].map((group) => {
        const special = group.filter((card) => card.is_founder || card.is_shiny);
        return (special.length ? special : group).sort(cardOrder)[0];
      });
    }
    return result.sort(cardOrder);
  }, [battleHoardIDs, cardKind, cards, combatHoardOnly, editionFilter, hidesDuplicates]);

  const packCounts = useMemo(() => sealedPacks.reduce<Record<string, number>>((counts, pack) => {
    counts[pack.edition] = (counts[pack.edition] ?? 0) + 1;
    return counts;
  }, {}), [sealedPacks]);
  const ownedEntitlements = useMemo(() => new Set(entitlements.map((item) => item.item)), [entitlements]);

  function cycleEdition() {
    const cycle: (Edition | "all")[] = ["all", ...availableEditions];
    setEditionFilter(nextValue(cycle, editionFilter));
    collectionHeading.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (isLoadingAccount) {
    return (
      <div className="account-page page-scene">
        <img src="/art/creatures/nibblewick.webp" className="login-creature" alt="NibbleWick guarding the login" />
        <section className="login-plaque login-plaque--loading" aria-live="polite" aria-busy="true">
          <img src="/art/brand/grinning-forge-emblem.webp" alt="" />
          <p className="eyebrow">The tide is turning</p>
          <h1>Opening your hoard.</h1>
          <p className="account-loading-copy">Syncing your island name, Battle Hoard, sealed packs, entitlements, and NUMGNUMS…</p>
          <div className="account-loading-runes" aria-hidden="true"><i /><i /><i /><i /><i /></div>
        </section>
      </div>
    );
  }

  if (isPasswordRecovery) {
    return (
      <div className="account-page page-scene">
        <img src="/art/creatures/nibblewick.webp" className="login-creature" alt="NibbleWick guarding the password forge" />
        <section className="login-plaque">
          <img src="/art/brand/grinning-forge-emblem.webp" alt="NUMGNUMS grinning forge" />
          <p className="eyebrow">Recovery flare received</p>
          <h1>Forge a new key.</h1>
          <p className="login-recovery-copy">Choose the new password that will open your Hoard.</p>
          <form onSubmit={(event) => void completePasswordRecovery(event)}>
            <label>New password<input type="password" autoComplete="new-password" minLength={8} value={recoveryPassword} onChange={(event) => setRecoveryPassword(event.target.value)} required /></label>
            <label>Confirm new password<input type="password" autoComplete="new-password" minLength={8} value={confirmedRecoveryPassword} onChange={(event) => setConfirmedRecoveryPassword(event.target.value)} required /></label>
            <GameButton type="submit" tone="gold" disabled={isChangingPassword}>{isChangingPassword ? "Forging…" : "Set new password"}</GameButton>
          </form>
          {message && <p className="form-message" role="status">{message}</p>}
          <button type="button" className="login-secondary-action" onClick={() => void cancelPasswordRecovery()}>Cancel recovery</button>
        </section>
      </div>
    );
  }

  if (accountSetupUserID) {
    return (
      <div className="account-page page-scene">
        <img src="/art/creatures/nibblewick.webp" className="login-creature" alt="NibbleWick guarding the account forge" />
        <section className="login-plaque login-plaque--signup">
          <img src="/art/brand/grinning-forge-emblem.webp" alt="NUMGNUMS grinning forge" />
          <p className="eyebrow">Apple sign-in connected</p>
          <h1>Finish saving my island.</h1>
          <p className="login-recovery-copy">Choose the public island name that will follow this Apple account into the website and game.</p>
          <form className="signup-form" onSubmit={(event) => void completeConnectedAccount(event)}>
            <label className="signup-form__wide" htmlFor="connected-account-username">
              Island name
              <input id="connected-account-username" type="text" autoComplete="username" maxLength={16} pattern="[A-Za-z][A-Za-z0-9]{2,15}" aria-describedby="connected-account-name-help" value={signupUsername} onChange={(event) => setSignupUsername(event.target.value.slice(0, 16))} required />
              <small id="connected-account-name-help" className="signup-help">3–16 letters and numbers, beginning with a letter. This name is public.</small>
            </label>
            <SignupTerms checked={acceptsSignupTerms} onChange={setAcceptsSignupTerms} />
            <div className="signup-form__wide"><GameButton type="submit" tone="gold" disabled={isSigningUp}>{isSigningUp ? "Saving island…" : "Save my Hoard"}</GameButton></div>
          </form>
          {message && <p className="form-message" role="status">{message}</p>}
          <button type="button" className="login-secondary-action" onClick={() => void signOut()}>Use another account</button>
          <p className="privacy-copy">Your public island name and cards can be viewed by other players. Your email, points, purchases, identifiers, and credentials remain private.</p>
        </section>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="account-page page-scene">
        <img src="/art/creatures/nibblewick.webp" className="login-creature" alt="NibbleWick guarding the login" />
        <section className={`login-plaque${loginMode === "sign-up" ? " login-plaque--signup" : ""}`}>
          <img src="/art/brand/grinning-forge-emblem.webp" alt="NUMGNUMS grinning forge" />
          {loginMode === "forgot-password" ? (
            <>
              <p className="eyebrow">Lost island key</p>
              <h1>Recover my hoard.</h1>
              <p className="login-recovery-copy">Enter your account email and we’ll send a secure recovery link.</p>
              <form onSubmit={(event) => void sendPasswordReset(event)}>
                <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
                <GameButton type="submit" tone="gold" disabled={isSendingPasswordReset}>{isSendingPasswordReset ? "Sending…" : "Send recovery link"}</GameButton>
              </form>
              {message && <p className="form-message" role="status">{message}</p>}
              <button type="button" className="login-secondary-action" onClick={() => { setLoginMode("sign-in"); setMessage(null); }}>Back to login</button>
            </>
          ) : loginMode === "sign-up" ? (
            <>
              <p className="eyebrow">A new island stirs</p>
              <h1>Claim my island.</h1>
              <p className="login-recovery-copy">Create one account for your website Hoard and the NUMGNUMS game.</p>
              <form className="signup-form" onSubmit={(event) => void signUp(event)}>
                <label className="signup-form__wide" htmlFor="signup-username">
                  Island name
                  <input id="signup-username" type="text" autoComplete="username" maxLength={16} pattern="[A-Za-z][A-Za-z0-9]{2,15}" aria-describedby="signup-name-help" value={signupUsername} onChange={(event) => setSignupUsername(event.target.value.slice(0, 16))} required />
                  <small id="signup-name-help" className="signup-help">3–16 letters and numbers, beginning with a letter. This name is public.</small>
                </label>
                <label className="signup-form__wide" htmlFor="signup-email">
                  Email
                  <input id="signup-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </label>
                <label htmlFor="signup-password">
                  Password
                  <input id="signup-password" type="password" autoComplete="new-password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} required />
                </label>
                <label htmlFor="signup-password-confirmation">
                  Confirm password
                  <input id="signup-password-confirmation" type="password" autoComplete="new-password" minLength={8} value={signupPasswordConfirmation} onChange={(event) => setSignupPasswordConfirmation(event.target.value)} required />
                </label>
                <SignupTerms checked={acceptsSignupTerms} onChange={setAcceptsSignupTerms} />
                <div className="signup-form__wide"><GameButton type="submit" tone="gold" disabled={isSigningUp}>{isSigningUp ? "Saving island…" : "Create my Hoard"}</GameButton></div>
              </form>
              {message && <p className="form-message" role="status">{message}</p>}
              <button type="button" className="login-secondary-action" onClick={() => { setLoginMode("sign-in"); setMessage(null); }}>Already have an account? Log in</button>
            </>
          ) : (
            <>
              <p className="eyebrow">Returning forger</p>
              <h1>Open my hoard.</h1>
              <form onSubmit={(event) => void signIn(event)}>
                <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
                <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
                <div className="login-mode-actions">
                  <button type="button" className="login-secondary-action" onClick={() => { setLoginMode("forgot-password"); setMessage(null); }}>Forgot password?</button>
                  <button type="button" className="login-secondary-action" onClick={() => { setLoginMode("sign-up"); setMessage(null); }}>Create an account</button>
                </div>
                <GameButton type="submit" tone="sea">Login to NUMGNUMS</GameButton>
              </form>
              {message && <p className="form-message" role="status">{message}</p>}
              <div className="login-divider"><span>or</span></div>
              <div className="apple-button-frame">
                <button type="button" className="apple-login" onClick={() => void signInWithApple()}>Continue with Apple</button>
              </div>
            </>
          )}
          <p className="privacy-copy">Your public island name and cards can be viewed by other players. Your email, points, purchases, identifiers, and credentials remain private.</p>
          {!hasSupabaseConfiguration && <span className="demo-ribbon">ISLAND SIGNAL OFFLINE</span>}
        </section>
      </div>
    );
  }

  return (
    <div className="hoard-page page-scene">
      <aside className="profile-totem">
        <img src="/art/creatures/nibblewick.webp" alt="Player portrait" />
        <h1>{profile.username}<VerifiedCreatorMark username={profile.username} /></h1>
        <p>{profile.wins} W · {profile.draws} D · {profile.losses} L</p>
        <div><span>{profile.play_points} play</span><span>{profile.win_points} wins</span></div>
        <GameButton onClick={() => void signOut()} tone="sea">Log out</GameButton>
      </aside>

      <section className="account-hoard">
        <header className="hoard-command-header">
          <div>
            <p className="eyebrow">Your slop. Your legacy.</p>
            <h2>My Hoard</h2>
            <p>Every serialized NUMGNUM, unopened pack, and island unlock attached to this account.</p>
          </div>
          <div className="hoard-command-stats" aria-label="Hoard totals">
            <span><b>{cards.length}</b> cards</span>
            <span><b>{battleHoardIDs.size}</b> in battle</span>
            <span><b>{sealedPacks.length}</b> sealed</span>
          </div>
        </header>

        {message && <p className="hoard-status-message" role="status">{message}</p>}

        <section className="pack-vault" aria-labelledby="pack-vault-title">
          <div className="hoard-section-heading">
            <div><p className="eyebrow">Fresh from the slop press</p><h3 id="pack-vault-title">Sealed Packs</h3></div>
            <strong>{sealedPacks.length} unopened</strong>
          </div>
          {!packInventoryOnline && <p className="inventory-warning">The pack vault has lost the island signal. Your cards and unlocks are still safe.</p>}
          <div className="pack-vault-grid">
            {packCatalog.map((pack) => (
              <article className={`pack-vault-card pack-vault-card--${pack.edition}`} key={pack.edition}>
                <div className="pack-vault-art" aria-hidden="true">
                  <img src={pack.art} alt="" />
                  <img src={pack.art} alt="" />
                  <img src={pack.art} alt="" />
                  <b>{packCounts[pack.edition] ?? 0}</b>
                </div>
                <div className="pack-vault-copy">
                  <span>{packCounts[pack.edition] ?? 0} currently sealed</span>
                  <h4>{pack.title}</h4>
                  <p>Five unopened packs. Contents roll from the live forged catalog when opened in the game.</p>
                  <GameButton tone={pack.tone} disabled={!packInventoryOnline || checkoutEdition !== null} onClick={() => void buyPackBundle(pack.edition)}>
                    {checkoutEdition === pack.edition ? "Opening checkout…" : "Buy 5 · $0.99"}
                  </GameButton>
                </div>
              </article>
            ))}
          </div>
          <p className="pack-purchase-note">Secure checkout is handled by Stripe. Paid packs are delivered to this same NUMGNUMS account and appear in MY HOARD on every synced device.</p>
        </section>

        <section className="entitlement-vault" aria-labelledby="entitlement-title">
          <div className="hoard-section-heading">
            <div><p className="eyebrow">Permanent island machinery</p><h3 id="entitlement-title">Unlocks & Entitlements</h3></div>
            <strong>{ownedEntitlements.size} online</strong>
          </div>
          <div className="entitlement-grid">
            {entitlementCatalog.map((item) => {
              const isOwned = ownedEntitlements.has(item.id);
              return (
                <article className={isOwned ? "is-owned" : "is-locked"} key={item.id}>
                  <span className="entitlement-icon" aria-hidden="true">{item.icon}</span>
                  <div><h4>{item.title}</h4><p>{item.detail}</p><b>{isOwned ? "ONLINE · UNLOCKED" : "NOT YET UNLOCKED"}</b></div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="account-collection" aria-labelledby="collection-title">
          <div className="hoard-section-heading" ref={collectionHeading}>
            <div><p className="eyebrow">Serialized and hungry</p><h3 id="collection-title">My Hoard</h3></div>
            <strong>{visibleCards.length} showing</strong>
          </div>
          <div className="hoard-filter-rack" aria-label="Card filters">
            <button type="button" onClick={cycleEdition} className={editionFilter !== "all" ? "is-active" : ""}>
              <span>Edition</span><b>{editionFilter === "all" ? "All Editions" : displayEdition(editionFilter)}</b><i>↻</i>
            </button>
            <button type="button" onClick={() => setHidesDuplicates((value) => !value)} className={hidesDuplicates ? "is-active" : ""}>
              <span>Copies</span><b>{hidesDuplicates ? "No Duplicates" : "Duplicates"}</b><i>▣</i>
            </button>
            <button type="button" onClick={() => setCardKind(nextValue(kindOrder, cardKind))} className={cardKind !== "all" ? "is-active" : ""}>
              <span>Finish</span><b>{kindLabels[cardKind]}</b><i>✦</i>
            </button>
            <button type="button" onClick={() => setCombatHoardOnly((value) => !value)} className={combatHoardOnly ? "is-active" : ""}>
              <span>Battle</span><b>{combatHoardOnly ? "Combat Hoard Only" : "Entire Hoard"}</b><i>◈</i>
            </button>
          </div>

          {visibleCards.length ? (
            <div className="account-card-grid">
              {visibleCards.map((card) => (
                <article className={`hoard-card-entry ${battleHoardIDs.has(card.id) ? "is-in-battle-hoard" : ""}`} key={card.id}>
                  <NumgnumCard level={card.level} edition={card.edition} shiny={card.is_shiny} founder={card.is_founder} serial={card.serial_number} founderName={card.founder_username} showsSerialBadge />
                  <div className="hoard-card-caption">
                    <strong>{NUMGNUM_NAMES[card.level] ?? `NUMGNUM ${card.level}`}</strong>
                    <span>{displayEdition(card.edition)}{card.is_shiny ? " · Shiny" : ""}{card.is_founder ? " · Founder" : ""}</span>
                  </div>
                  {battleHoardIDs.has(card.id) && <b className="battle-hoard-rivet">Battle Hoard</b>}
                </article>
              ))}
            </div>
          ) : (
            <div className="hoard-empty-state"><img src="/art/brand/grinning-forge-emblem.webp" alt="" /><h4>No bites in this filter.</h4><p>Cycle a filter to let the NUMGNUMS back into view.</p></div>
          )}
        </section>

        <details className="account-settings">
          <summary>
            <span>
              <small>Account settings</small>
              <strong>{passwordSignInAvailable ? "Change password" : "Sign-in details"}</strong>
            </span>
          </summary>
          <div className="account-settings__panel">
            {passwordSignInAvailable ? (
              <>
                <p>Change the password used to open this saved island with email or player name.</p>
                <form className="password-change-form" onSubmit={(event) => void changePassword(event)}>
                  <label>
                    Current password
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(event) => { setCurrentPassword(event.target.value); setPasswordMessage(null); }}
                      required
                    />
                  </label>
                  <label>
                    New password
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={newPassword}
                      onChange={(event) => { setNewPassword(event.target.value); setPasswordMessage(null); }}
                      required
                    />
                  </label>
                  <label>
                    Confirm new password
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={confirmedNewPassword}
                      onChange={(event) => { setConfirmedNewPassword(event.target.value); setPasswordMessage(null); }}
                      required
                    />
                  </label>
                  <GameButton type="submit" tone="sea" disabled={isChangingPassword}>
                    {isChangingPassword ? "Changing key…" : "Change Password"}
                  </GameButton>
                </form>
              </>
            ) : (
              <p>This island uses Sign in with Apple. Apple protects the sign-in, so there is no NUMGNUMS password to change.</p>
            )}
            {passwordMessage && <p className="password-change-message" role="status">{passwordMessage}</p>}
          </div>
        </details>
      </section>
    </div>
  );
}
